import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { useAuthContext } from '@/context/AuthContext';
import { useReferral } from '@/hooks/useReferral';
import { ReferralErrorModal } from '@/components/ReferralErrorModal';
import {
  parseReferralCodeFromUrl,
  storePendingReferralCode,
  getPendingReferralCode,
  clearPendingReferralCode,
  isValidInviteCodeFormat,
} from '@/lib/referral';

/**
 * WatchReferral component
 *
 * Monitors URL parameters and authentication state for automatic referral code binding.
 * Flow:
 * 1. On mount, checks for ?ref= or ?referral= in URL
 * 2. If found and user not authenticated, stores code as pending in localStorage
 * 3. After successful authentication, automatically binds pending code
 * 4. Clears pending code after successful bind or on error
 *
 * This component has no UI - it only manages side effects.
 */
function WatchReferral() {
  const { isAuthenticated } = useAuthContext();
  const { bindCode, hasInviter, checkCode, bindError, clearBindError } = useReferral();
  const hasProcessedUrl = useRef(false);
  const hasAttemptedBind = useRef(false);

  // Use refs to store latest function references to avoid them as dependencies
  const bindCodeRef = useRef(bindCode);
  const checkCodeRef = useRef(checkCode);

  // Update refs when functions change
  useEffect(() => {
    bindCodeRef.current = bindCode;
    checkCodeRef.current = checkCode;
  }, [bindCode, checkCode]);

  // Step 1: Process URL parameter on mount
  useEffect(() => {
    if (hasProcessedUrl.current) return;

    const codeFromUrl = parseReferralCodeFromUrl();
    if (codeFromUrl && isValidInviteCodeFormat(codeFromUrl)) {
      storePendingReferralCode(codeFromUrl);
      console.log('Referral code detected from URL:', codeFromUrl);

      if (!isAuthenticated) {
        toast.info('Sign in to join this referral network!');
      }
    }

    hasProcessedUrl.current = true;
  }, [isAuthenticated]);

  // Step 2: Auto-bind pending code after authentication
  useEffect(() => {
    const autoBind = async () => {
      // Don't bind if already attempted, not authenticated, or already has inviter
      if (hasAttemptedBind.current || !isAuthenticated || hasInviter) {
        return;
      }

      const pendingCode = getPendingReferralCode();
      if (!pendingCode) return;

      // Validate format before making API call
      if (!isValidInviteCodeFormat(pendingCode)) {
        console.warn('Pending referral code has invalid format:', pendingCode);
        clearPendingReferralCode();
        return;
      }

      hasAttemptedBind.current = true;

      try {
        // Validate code exists on backend before attempting to bind
        console.log('Validating pending referral code:', pendingCode);
        const isValid = await checkCodeRef.current(pendingCode);

        if (!isValid) {
          toast.error(`Referral code ${pendingCode} is invalid or not found`);
          clearPendingReferralCode();
          hasAttemptedBind.current = false;
          return;
        }

        // Bind the code
        console.log('Auto-binding referral code:', pendingCode);
        bindCodeRef.current(pendingCode);
        clearPendingReferralCode();

        // Success toast is handled by useReferral hook's onSuccess callback
      } catch (error) {
        console.error('Auto-bind referral code failed:', error);
        toast.error('Failed to join referral network. Please try again.');
        hasAttemptedBind.current = false; // Allow retry
      }
    };

    // Small delay to ensure auth is fully settled and hooks are ready
    if (isAuthenticated) {
      const timer = setTimeout(autoBind, 1000);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, hasInviter]);

  // Step 3: Reset bind attempt when user disconnects
  useEffect(() => {
    if (!isAuthenticated) {
      hasAttemptedBind.current = false;
    }
  }, [isAuthenticated]);

  // No UI needed - except for error modal
  return (
    <>
      {bindError && (
        <ReferralErrorModal
          open={!!bindError}
          onClose={clearBindError}
          error={bindError}
        />
      )}
    </>
  );
}

export default WatchReferral;
